
import { useEffect, useMemo, useState } from "react"

import { useGlobal } from "../../../contexts/GlobalContext"
import { auth_axios } from "../../../libs/authWeb"
import { mixpanel_client_track } from "../../../libs/mixpanelClient"

const discover_bucket_labels = {
  kalshi_markets: "Kalshi markets",
  kalshi_events: "Kalshi events",
  polymarket_markets: "Polymarket markets",
  polymarket_events: "Polymarket events",
}

const discover_bucket_order = [
  "kalshi_markets",
  "kalshi_events",
  "polymarket_markets",
  "polymarket_events",
]

const format_date_or_dash = (value) => {
  if (!value) {
    return "—"
  }
  try {
    return new Date(value).toLocaleString()
  }
  catch {
    return "—"
  }
}

const get_freshness = (updated_at) => {
  if (!updated_at) {
    return { label: "unknown", color: "text-gray-500 bg-gray-100" }
  }
  const age_ms = Date.now() - new Date(updated_at).getTime()
  if (Number.isNaN(age_ms) || age_ms < 0) {
    return { label: "unknown", color: "text-gray-500 bg-gray-100" }
  }
  if (age_ms <= 5 * 60 * 1000) {
    return { label: "fresh", color: "text-green-700 bg-green-100" }
  }
  if (age_ms <= 30 * 60 * 1000) {
    return { label: "warming", color: "text-amber-700 bg-amber-100" }
  }
  return { label: "stale", color: "text-red-700 bg-red-100" }
}

const HomePage = () => {
  const global_context = useGlobal()

  const [discover_buckets, set_discover_buckets] = useState<any>({})
  const [ws_status, set_ws_status] = useState<any>({})
  const [ws_intents, set_ws_intents] = useState<any>({})

  const [is_loading, set_is_loading] = useState(false)
  const [error_text, set_error_text] = useState("")
  const [auto_refresh_enabled, set_auto_refresh_enabled] = useState(true)
  const [last_refreshed_at, set_last_refreshed_at] = useState(null)

  const load_control_panel_data = async () => {
    set_is_loading(true)
    set_error_text("")

    try {
      const [discover_res, ws_status_res, ws_intents_res] = await Promise.all([
        auth_axios.get("/api/discover/status"),
        auth_axios.get("/api/ws/status"),
        auth_axios.get("/api/ws/intents"),
      ])

      if (discover_res.data?.success) {
        set_discover_buckets(discover_res.data.buckets || {})
      }
      else {
        throw new Error(discover_res.data?.status || "DISCOVER_STATUS_ERROR")
      }

      if (ws_status_res.data?.success) {
        set_ws_status(ws_status_res.data || {})
      }
      else {
        throw new Error(ws_status_res.data?.status || "WS_STATUS_ERROR")
      }

      if (ws_intents_res.data?.success) {
        set_ws_intents(ws_intents_res.data.intents || {})
      }
      else {
        throw new Error(ws_intents_res.data?.status || "WS_INTENTS_READ_ERROR")
      }

      set_last_refreshed_at(new Date().toISOString())
    }
    catch (error) {
      const msg =
        error?.response?.data?.status
        || error?.response?.data?.message
        || error?.message
        || "Control panel load failed."
      set_error_text(msg)
    }
    finally {
      set_is_loading(false)
    }
  }

  useEffect(() => {
    void load_control_panel_data()
    mixpanel_client_track("app_dashboard_home_control_panel_visited", global_context.user_id)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, [])

  useEffect(() => {
    if (!auto_refresh_enabled) {
      return undefined
    }
    const interval_id = window.setInterval(() => {
      void load_control_panel_data()
    }, 4000)
    return () => window.clearInterval(interval_id)
  // eslint-disable-next-line react-hooks/exhaustive-deps -- interval controlled by toggle only
  }, [auto_refresh_enabled])

  const ws_counts = useMemo(() => {
    return {
      kalshi_channels: Array.isArray(ws_intents?.kalshi?.channels) ? ws_intents.kalshi.channels.length : 0,
      kalshi_market_tickers: Array.isArray(ws_intents?.kalshi?.market_tickers) ? ws_intents.kalshi.market_tickers.length : 0,
      polymarket_asset_ids: Array.isArray(ws_intents?.polymarket_market?.asset_ids) ? ws_intents.polymarket_market.asset_ids.length : 0,
      polymarket_user_markets: Array.isArray(ws_intents?.polymarket_user?.markets) ? ws_intents.polymarket_user.markets.length : 0,
    }
  }, [ws_intents])

  return (
    <div className="px-4 py-8 pb-32 md:py-16 sm:px-6 md:flex-auto md:px-0 md:py-8">
      <div className="mx-auto max-w-5xl space-y-6 md:mx-0 md:max-w-none">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold leading-7 text-gray-900">Control Panel</h2>
            <p className="mt-1 text-sm leading-6 text-gray-500">
              Server runtime and data pipeline status.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-blue-300 hover:text-blue-700 disabled:opacity-50"
              onClick={() => void load_control_panel_data()}
              disabled={is_loading}
            >
              {is_loading ? "Refreshing..." : "Refresh now"}
            </button>
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:border-blue-300 hover:text-blue-700"
              onClick={() => set_auto_refresh_enabled((prev) => !prev)}
            >
              Auto refresh: {auto_refresh_enabled ? "on" : "off"}
            </button>
          </div>
        </div>

        <div className="text-xs text-gray-500">
          Last refreshed: {format_date_or_dash(last_refreshed_at)}
        </div>

        {error_text ? (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {error_text}
          </div>
        ) : null}

        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900">App Session</h3>
          <div className="mt-3 grid gap-2 text-sm text-gray-700 sm:grid-cols-2">
            <div>User ID: <span className="font-medium">{global_context.user_id || "UNSET"}</span></div>
            <div>Type: <span className="font-medium">{global_context.type || "UNSET"}</span></div>
            <div>Authenticated: <span className="font-medium">{global_context.authenticated ? "true" : "false"}</span></div>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900">Discover Cache Health</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase text-gray-500">
                  <th className="py-2 pr-4">Bucket</th>
                  <th className="py-2 pr-4">State</th>
                  <th className="py-2 pr-4">Updated</th>
                  <th className="py-2 pr-4">Count</th>
                  <th className="py-2">Owner / Started</th>
                </tr>
              </thead>
              <tbody>
                {discover_bucket_order.map((bucket_id) => {
                  const row = discover_buckets[bucket_id] || {}
                  const freshness = get_freshness(row.updated_at)
                  return (
                    <tr key={bucket_id} className="border-b border-gray-100">
                      <td className="py-2 pr-4 font-medium text-gray-900">
                        {discover_bucket_labels[bucket_id] || bucket_id}
                      </td>
                      <td className="py-2 pr-4 text-gray-700">
                        <div className="flex items-center gap-2">
                          <span>{row.fetching ? "fetching" : "idle"}</span>
                          <span className={`rounded px-1.5 py-0.5 text-xs font-semibold ${freshness.color}`}>
                            {freshness.label}
                          </span>
                        </div>
                      </td>
                      <td className="py-2 pr-4 text-gray-700">{format_date_or_dash(row.updated_at)}</td>
                      <td className="py-2 pr-4 text-gray-700">{row.count ?? 0}</td>
                      <td className="py-2 text-xs text-gray-600">
                        <div>{row.owner_id || row.last_fetch_owner_id || "—"}</div>
                        <div>{format_date_or_dash(row.fetch_started_at || row.last_fetch_started_at)}</div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900">WebSocket Runtime Health</h3>
          <div className="mt-3 grid gap-2 text-sm text-gray-700 sm:grid-cols-2">
            <div>Local dyno is leader: <span className="font-medium">{ws_status.is_leader ? "true" : "false"}</span></div>
            <div>Local owner id: <span className="font-medium">{ws_status.owner_id || "—"}</span></div>
            <div>Cluster leader owner id: <span className="font-medium">{ws_status.cluster_leader_owner_id || "—"}</span></div>
            <div>Kalshi channels: <span className="font-medium">{ws_counts.kalshi_channels}</span></div>
            <div>Kalshi market tickers: <span className="font-medium">{ws_counts.kalshi_market_tickers}</span></div>
            <div>Polymarket asset ids: <span className="font-medium">{ws_counts.polymarket_asset_ids}</span></div>
            <div>Polymarket user markets: <span className="font-medium">{ws_counts.polymarket_user_markets}</span></div>
          </div>
        </section>

        <section className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900">Raw Runtime Snapshots</h3>
          <div className="mt-3 space-y-3 text-xs text-gray-700">
            <details>
              <summary className="cursor-pointer font-semibold">`/api/discover/status` buckets</summary>
              <pre className="mt-2 overflow-auto rounded bg-gray-50 p-2">{JSON.stringify(discover_buckets, null, 2)}</pre>
            </details>
            <details>
              <summary className="cursor-pointer font-semibold">`/api/ws/status`</summary>
              <pre className="mt-2 overflow-auto rounded bg-gray-50 p-2">{JSON.stringify(ws_status, null, 2)}</pre>
            </details>
            <details>
              <summary className="cursor-pointer font-semibold">`/api/ws/intents`</summary>
              <pre className="mt-2 overflow-auto rounded bg-gray-50 p-2">{JSON.stringify(ws_intents, null, 2)}</pre>
            </details>
          </div>
        </section>
      </div>
    </div>
  )
}

export default HomePage